import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { useAsync } from '../../api/useAsync';
import type {
  ClusterNode,
  ResourceKind,
  Scope,
  ScopeFallback,
  ScopeHostKey,
} from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { ErrorAlert } from '../../ui/Alert';
import { Button } from '../../ui/primitives';
import { SelectField, TagPicker, TextField } from '../../ui/form';
import type { PickerOption } from '../../ui/form';
import { shortGap } from '../../ui/ago';
import { Drawer } from '../../ui/Drawer';
import { KIND_LABEL } from '../../ui/ResourceTable';
import { isApiError } from '../../api/errors';
import { checkScope } from './scopeForm';
import type { ScopeField } from './scopeForm';
import type { ApiError } from '../../api/errors';

/**
 * Declaring a boundary, or rewriting one.
 *
 * The same surface every other row of this console is written on: a drawer over
 * the list it came from, the fields as labelled boxes with their hints, and the
 * two buttons in the footer. A boundary is a standing permission to write
 * catalogue rows, so the form says what each field admits rather than leaving a
 * bare input for somebody to guess at.
 *
 * The prefix is the address the rows are written under and is fixed once the
 * boundary exists: changing it would be declaring a second boundary and taking
 * the first away, and taking one away deletes the rows it held.
 */
export function ScopeDrawer({
  scope,
  kinds,
  onClose,
  onWritten,
}: {
  /** Absent when this is a new boundary. */
  scope?: Scope;
  /** The kinds this tenancy's build can serve, in the order the console lists. */
  kinds: readonly ResourceKind[];
  onClose: () => void;
  onWritten: () => void;
}) {
  const api = useClusterApi();
  const { t } = useI18n();
  const existing = scope !== undefined;
  const [prefix, setPrefix] = useState(scope?.prefix ?? '');
  const [node, setNode] = useState(scope?.node ?? '');
  const [admitted, setAdmitted] = useState<string[]>(scope?.kinds ?? []);
  // The registry, for the connector list. A node that has not joined cannot
  // fill a boundary, so what can be chosen is exactly what is enrolled.
  const nodes = useAsync<ClusterNode[]>((signal) => api.listNodes(signal), 'scope-nodes');
  // Read once, when the drawer opens: an age that ticks under a form is a
  // field that changes while somebody reads it.
  const [openedAt] = useState(() => Date.now());
  const connectors = useMemo(() => {
    if (nodes.status !== 'ready') {
      return [];
    }
    const now = openedAt;
    return nodes.value
      .filter((candidate) => candidate.role === 'connector' && candidate.revoked_at === undefined)
      .map((candidate) => ({
        value: candidate.id,
        // What the node called itself, falling back to the id: the name is the
        // node's own claim, and it is what a person recognises.
        label: candidate.claimed_name ?? candidate.id,
        detail: t('time.ago', { value: shortGap(candidate.enrolled_at * 1000, now) }),
      }));
  }, [nodes, openedAt, t]);
  const [project, setProject] = useState(scope?.project ?? 'default');
  const [environment, setEnvironment] = useState(scope?.environment ?? '');
  const [maxLive, setMaxLive] = useState(String(scope?.max_live ?? 100));
  const [leaseSecs, setLeaseSecs] = useState(String(scope?.lease_secs ?? 900));
  const [fallback, setFallback] = useState<ScopeFallback>(scope?.fallback ?? 'parent');
  const [hostKey, setHostKey] = useState<ScopeHostKey>(scope?.ssh_host_key ?? 'annotation');
  const kindOptions: PickerOption[] = kinds.map((kind) => ({
    value: kind,
    label: t(KIND_LABEL[kind]),
    detail: kind,
  }));
  const [failure, setFailure] = useState<ApiError | string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<ScopeField, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // Checked here rather than left to the cluster: every one of these is a
    // box on this screen, and a refusal that comes back as a status code names
    // none of them.
    const found = checkScope({
      prefix,
      node,
      kinds: admitted,
      environment,
      maxLive,
      leaseSecs,
    });
    const shown: Partial<Record<ScopeField, string>> = {};
    for (const field of Object.keys(found) as ScopeField[]) {
      const key = found[field];
      if (key !== undefined) shown[field] = t(key);
    }
    setErrors(shown);
    setFailure(null);
    if (Object.keys(shown).length > 0) {
      return;
    }
    setSubmitting(true);
    try {
      await api.putScope(prefix, {
        node,
        kinds: admitted as ResourceKind[],
        project,
        environment,
        max_live: Number(maxLive),
        lease_secs: Number(leaseSecs),
        fallback,
        ssh_host_key: hostKey,
      });
      onWritten();
    } catch (error) {
      if (isApiError(error)) {
        // The cluster names a box where it can; where it names `scope`, the
        // sentence is about the declaration as a whole and stays on the alert.
        const named: Partial<Record<ScopeField, string>> = {};
        for (const field of ['prefix', 'node', 'kinds', 'environment'] as ScopeField[]) {
          const reason = error.fieldError(field);
          if (reason !== undefined) named[field] = reason;
        }
        setErrors(named);
        setFailure(error);
      } else {
        setFailure(String(error));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={t(existing ? 'scopes.edit.title' : 'scopes.new.title')}
      subtitle={t('scopes.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="primary" form="scope" type="submit" disabled={submitting}>
            {submitting ? `${t('scopes.submitting')}…` : t('scopes.submit')}
          </Button>
        </>
      }
    >
      {failure !== null && (
        <div style={{ marginBottom: 18 }}>
          {isApiError(failure) ? <ErrorAlert error={failure} /> : <p className="t-sub">{failure}</p>}
        </div>
      )}

      <form id="scope" onSubmit={(event) => void onSubmit(event)} noValidate>
        <TextField
          label={t('scopes.field.prefix')}
          hint={t(existing ? 'scopes.prefix.fixed' : 'scopes.prefix.hint')}
          placeholder={t('scopes.prefix.placeholder')}
          mono
          readOnly={existing}
          value={prefix}
          onChange={(event) => {
            setPrefix(event.target.value);
          }}
          {...(errors.prefix === undefined ? {} : { error: errors.prefix })}
        />
        {/* The registry, by name, with how long ago each joined — two
            connectors of one deployment differ by little else. What is stored
            is the id the cluster knows the node by. */}
        <SelectField
          label={t('scopes.field.node')}
          hint={t(nodes.status === 'error' ? 'scopes.node.unreadable' : 'scopes.node.hint')}
          value={node}
          onChange={(event) => {
            setNode(event.target.value);
          }}
          {...(errors.node === undefined ? {} : { error: errors.node })}
        >
          <option value="">{t('scopes.node.unset')}</option>
          {connectors.map((connector) => (
            <option key={connector.value} value={connector.value}>
              {`${connector.label} (${connector.detail})`}
            </option>
          ))}
          {/* A boundary that names a node the registry no longer lists keeps
              naming it: the form shows what is stored rather than dropping it. */}
          {node !== '' && !connectors.some((connector) => connector.value === node) && (
            <option value={node}>{node}</option>
          )}
        </SelectField>
        <TagPicker
          label={t('scopes.field.kinds')}
          hint={t('scopes.kinds.hint')}
          placeholder={t('scopes.kinds.placeholder')}
          removeLabel={t('scopes.kinds.remove')}
          options={kindOptions}
          chosen={admitted}
          onChange={setAdmitted}
          {...(errors.kinds === undefined ? {} : { error: errors.kinds })}
        />
        <TextField
          label={t('scopes.field.project')}
          hint={t('scopes.project.hint')}
          mono
          value={project}
          onChange={(event) => {
            setProject(event.target.value);
          }}
        />
        <TextField
          label={t('scopes.field.environment')}
          hint={t('scopes.environment.hint')}
          mono
          placeholder={t('scopes.environment.placeholder')}
          value={environment}
          onChange={(event) => {
            setEnvironment(event.target.value);
          }}
          {...(errors.environment === undefined ? {} : { error: errors.environment })}
        />
        <TextField
          label={t('scopes.field.maxLive')}
          hint={t('scopes.maxLive.hint')}
          mono
          inputMode="numeric"
          value={maxLive}
          onChange={(event) => {
            setMaxLive(event.target.value);
          }}
          {...(errors.maxLive === undefined ? {} : { error: errors.maxLive })}
        />
        <TextField
          label={t('scopes.field.leaseSecs')}
          hint={t('scopes.leaseSecs.hint')}
          mono
          inputMode="numeric"
          value={leaseSecs}
          onChange={(event) => {
            setLeaseSecs(event.target.value);
          }}
          {...(errors.leaseSecs === undefined ? {} : { error: errors.leaseSecs })}
        />
        <SelectField
          label={t('scopes.field.fallback')}
          hint={t('scopes.fallback.hint')}
          value={fallback}
          onChange={(event) => {
            setFallback(event.target.value as ScopeFallback);
          }}
        >
          <option value="parent">{t('scopes.fallback.parent')}</option>
          <option value="deny">{t('scopes.fallback.deny')}</option>
        </SelectField>
        <SelectField
          label={t('scopes.field.hostKey')}
          hint={t('scopes.hostKey.hint')}
          value={hostKey}
          onChange={(event) => {
            setHostKey(event.target.value as ScopeHostKey);
          }}
        >
          <option value="annotation">{t('scopes.hostKey.annotation')}</option>
          <option value="tofu">{t('scopes.hostKey.tofu')}</option>
        </SelectField>
      </form>
    </Drawer>
  );
}
